import { memo, useEffect, useState } from "react";

import { useTranslate } from "helpers";
import { getFleetVehicleAPI } from "services/api/fleet/vehicles/api";
import { initialFleetVehicleEditForm } from "services/api/fleet/vehicles/consts";
import { transformFleetVehicleEditFormValues } from "services/api/fleet/vehicles/transformations";
import { FleetVehicleEditFormValues } from "services/api/fleet/vehicles/types";

import { Button } from "ui/components/Button/Button";
import { Loader } from "ui/components/Loader";
import { Modal } from "ui/components/Modal";
import ModalBody from "ui/components/Modal/ModalBody";
import ModalFooter from "ui/components/Modal/ModalFooter";
import ModalHeader from "ui/components/Modal/ModalHeader";
import { FLEET_VEHICLE_DOCUMENT_CREATE_PAGE_TITLE } from "ui/components/PageTitle/consts";
import { initialVehicleInfo } from "ui/modules/fleet/vehicles/pages/VehicleDetailPage/components/VehicleDetailCard/components/VehicleEditCard/consts";
import VehicleDocumentForm from "ui/modules/fleet/vehicles/pages/VehicleDetailPage/components/VehicleDetailCard/components/VehicleEditCard/components/VehicleDocumentForm/VehicleDocumentForm";
import { VehicleInfo } from "ui/modules/fleet/vehicles/pages/VehicleDetailPage/components/VehicleDetailCard/components/VehicleEditCard/components/VehicleSummaryCard/types";
import VehicleWizardIntro from "ui/modules/fleet/vehicles/pages/VehicleDetailPage/components/VehicleDetailCard/components/VehicleEditCard/components/VehicleWizardIntro/VehicleWizardIntro";

import { VehicleDocumentAddCardProps } from "./types";

const DOCUMENTS_STEP_KEY = "documents";

/**
 * Unos dokumenata vozila izvan čarobnjaka: isti korak "Dokumenti" i ista intro scena kao u
 * čarobnjaku vozila, da unos izgleda i radi jednako gdje god se pokrene.
 */
const VehicleDocumentAddCard: React.FC<VehicleDocumentAddCardProps> = ({ show, vehicleId, onClose }) => {
  const { t } = useTranslate();
  const [formValues, setFormValues] = useState<FleetVehicleEditFormValues>(initialFleetVehicleEditForm);
  const [vehicleInfo, setVehicleInfo] = useState<VehicleInfo>(initialVehicleInfo);
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    if (show && vehicleId) fetchVehicle(vehicleId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, vehicleId]);

  const fetchVehicle = async (id: string) => {
    setIsFetching(true);
    try {
      const vehicle = await getFleetVehicleAPI(id);

      if (vehicle) {
        setFormValues(transformFleetVehicleEditFormValues(vehicle));
        // Kartica vozila u intru: naziv, slika i registracija, kao u čarobnjaku.
        setVehicleInfo({
          ...initialVehicleInfo,
          vehicleManufacturerName: vehicle.vehicleManufacturer?.name ?? null,
          vehicleModelName: vehicle.vehicleModel?.name ?? null,
          vehicleTypeName: vehicle.vehicleType?.name ?? null,
          vehicleImage: vehicle.vehicleType?.image ?? initialVehicleInfo.vehicleImage,
          registrationPlate: vehicle.registrationPlate ?? null,
          vinNumber: vehicle.vinNumber ?? null,
          kmTraveled: vehicle.kmTraveled ?? null,
        });
      }
    } catch {
      // greška se prikazuje kroz snackbar
    }
    setIsFetching(false);
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="full"
      position="side"
      intro={{
        content: (
          <VehicleWizardIntro
            vehicleInfo={vehicleInfo}
            stepKey={DOCUMENTS_STEP_KEY}
            stepKeys={[]}
          />
        ),
      }}
    >
      <ModalHeader
        icon="/media/images/add-icon.svg"
        title={FLEET_VEHICLE_DOCUMENT_CREATE_PAGE_TITLE.text}
        onHide={handleClose}
      />
      <ModalBody>
        {isFetching || !formValues.id ? (
          <div className="relative min-h-[60vh] xl:min-h-[600px] h-[calc(100vh-160px)]">
            <Loader isLoading={true}>{null}</Loader>
          </div>
        ) : (
          <VehicleDocumentForm
            formValues={formValues}
            setFormValues={setFormValues}
            setVehicleInfo={setVehicleInfo}
          />
        )}
      </ModalBody>
      <ModalFooter>
        <div className="px-4 py-3 flex justify-end gap-3">
          <Button
            variant="outlined"
            children={t("admin.close")}
            onClick={handleClose}
          />
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default memo(VehicleDocumentAddCard);
